import React from "react";

import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";
import XeroSyncButton from "../components/xero-sync-button";
import AddClientModal from "../components/popup/add-client";
import AddBtn from "../components/ui/add-btn";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import clientImage from '../files/images/staff.png'; // Using staff image for clients
import DynamicTableVirtual from "../components/table-virtual";
import { useMediaQuery, useTheme, Button, Box, Fab, IconButton, Menu as MuiMenu, MenuItem, Tooltip, Typography } from "@mui/material";
import { Menu, Plus, Settings } from "lucide-react";
import ClientInvoicePanel, { ClientInvoiceStats, summarizeClientInvoices, combineClientStats, invoiceTrend, InvoiceTrend } from "../components/client-invoice-panel";import { useSettings } from "../providers/settings-provider";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { useUser } from "../providers/user-provider";
import { useToast } from "../providers/toaster-provider";
import { usePermissions } from "../providers/permissions-provider";
import type { Client as ClientType } from "../components/popup/add-client";

// The $ Last 12 Months figure's colour: the year's trend (blue up, red down).
const TREND_COLORS: Record<InvoiceTrend['direction'], string> = { up: '#1e6fd9', down: '#d32f2f', flat: 'var(--color-default-text)' };

type ClientTableRow = {
    id: number;
    abbr: string;
    company: string;
    recipient: string;
    address: string;
    phone: string;
    email: string;
}

function formatAddress(client: { address1?: string | null; address2?: string | null; suburb?: string | null; state?: string | null; postalCode?: string | null; country?: string | null; }): string {
    const line1 = [client.address1, client.address2].filter(Boolean).join(' ');
    const line2 = [client.suburb, client.state, client.postalCode].filter(Boolean).join(' ');
    const parts = [line1, line2, client.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
}

const Clients: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    const canCreate = can('clients', 'create');
    const canUpdate = can('clients', 'update');
    const canDelete = can('clients', 'delete');
    const { showError, showSuccess } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedClient, setSelectedClient] = React.useState<ClientTableRow | null>(null);
    const [data, setData] = React.useState<ClientTableRow[]>([]);
    const list = useListSearch(data);
    const { formatAmount } = useSettings();
    // Invoice count/total per client, overall and per month (client-controller.js
    // getClientInvoiceStats), keyed by client id - shown in each row's expanded
    // panel, and only for users who can see invoices.
    const canSeeInvoices = can('invoices', 'read');
    const [invoiceStats, setInvoiceStats] = React.useState<Record<number, ClientInvoiceStats>>({});
    const [fetchInvoiceStats, setFetchInvoiceStats] = useApi();
    const [sortBy, setSortBy] = React.useState<string>('');
    const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

    // API hooks
    const [fetchClients, setFetchClients] = useApi();
    const [createClient, setCreateClient] = useApi();
    const [updateClient, setUpdateClient] = useApi();
    const [deleteClient, setDeleteClient] = useApi();
    const [analyzeAll, setAnalyzeAll] = useApi();
    const [settingsMenuAnchor, setSettingsMenuAnchor] = React.useState<null | HTMLElement>(null);

    React.useEffect(() => {
        document.title = "FineAnts | Clients";
        if (user?.entityId) {
            const params = [
                { param: 'entityId', value: user.entityId },
                { param: 'sortBy', value: sortBy },
                { param: 'sortDirection', value: sortDirection },
            ];
            const { url, method } = buildApiRequestConfig(routes.client.fetchByEntity, params);
            setFetchClients({ url, method });
        }
    }, [user, createClient, updateClient, deleteClient, analyzeAll, sortBy, sortDirection]);

    React.useEffect(() => {
        if (!user?.entityId || !canSeeInvoices) return;
        const { url, method } = buildApiRequestConfig(routes.client.invoiceStats, [{ param: 'entityId', value: user.entityId }]);
        setFetchInvoiceStats({ url, method });
    }, [user, canSeeInvoices, createClient, updateClient, setFetchInvoiceStats]);

    React.useEffect(() => {
        const { data: statsData, isLoading } = fetchInvoiceStats;
        if (statsData && !isLoading) {
            setInvoiceStats(Object.fromEntries(statsData.map((r: ClientInvoiceStats & { clientId: number }) => [r.clientId, r])));
        }
    }, [fetchInvoiceStats]);

    React.useEffect(() => {
        const { data: clientsData, isLoading, error } = fetchClients;
        if (clientsData && !isLoading) {
            // Transform API data to table format
            const transformedData: ClientTableRow[] = clientsData.map((client: any) => ({
                id: client.id,
                abbr: client.abbr || 'N/A',
                company: client.companyName,
                recipient: client.recipient,
                address: formatAddress(client),
                phone: client.phone || 'N/A',
                email: client.email || 'N/A',
                fax: client.fax || 'N/A',
            }));
            setData(transformedData);
        }
        if (error) {
            showError("Failed to fetch clients");
        }
    }, [fetchClients, showError, setData]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteClient;
        if (data && !isLoading) {
            showSuccess("Company deleted successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to delete company");
        }
    }, [deleteClient]);

    React.useEffect(() => {
        const { data, isLoading, error } = analyzeAll;
        if (data && !isLoading) {
            const skipped = data.skippedHasValue + data.skippedNoMatch;
            if (data.updated > 0) {
                showSuccess(`Suggested a bank description for ${data.updated} of ${data.total} client${data.total === 1 ? '' : 's'}${skipped > 0 ? ` (${skipped} skipped - already set or no paid transactions found)` : ''}`);
            } else {
                showError(`Nothing to suggest for any of the ${data.total} client${data.total === 1 ? '' : 's'} - already set, or no paid transactions found`);
            }
        }
        if (error) {
            showError("Failed to analyze bank descriptions");
        }
    }, [analyzeAll]);

    const handleAnalyzeAll = () => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.client.analyzeAllSearchStrings, [{ param: 'entityId', value: user.entityId }]);
        setAnalyzeAll({ url, method, body: {} });
    };

    React.useEffect(() => {
        const { data, isLoading, error } = createClient;
        if (data && !isLoading) {
            showSuccess("Company added successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to create company");
        }
    }, [createClient]);

    React.useEffect(() => {
        const { data, isLoading, error } = updateClient;
        if (data && !isLoading) {
            showSuccess("Company updated successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to update company");
        }
    }, [updateClient]);

    const handleEdit = (row: Record<string, any>) => {
        setSelectedClient(row as ClientTableRow);
        setOpenModal('edit');
    };

    const handleDelete = (row: Record<string, any>) => {
        setSelectedClient(row as ClientTableRow);
        setOpenModal('delete');
    };

    const handleConfirmDelete = () => {
        if (selectedClient) {
            const { url, method } = buildApiRequestConfig(routes.client.delete, [{ param: 'id', value: selectedClient.id }]);
            setDeleteClient({ url, method });
        }
    };

    const handleCreateClient = (clientData: ClientType) => {
        const { url, method } = buildApiRequestConfig(routes.client.create);
        const body = {
            entityId: user?.entityId,
            abbr: clientData.companyAbbreviation,
            companyName: clientData.company,
            recipient: clientData.recipient,
            address1: clientData.streetAddress1,
            address2: clientData.streetAddress2,
            state: clientData.state,
            postalCode: clientData.postalCode,
            country: clientData.country,
            suburb: clientData.suburb,
            phone: clientData.phone,
            fax: clientData.fax,
            email: clientData.email,
            nextInvoiceNumber: clientData.nextInvoiceNumber,
            provisionDateOffset: clientData.provisionDateOffset,
            isProvisionDateManual: clientData.isProvisionDateManual,
            searchString: clientData.searchString,
        }
        setCreateClient({ url, method, body });
    };

    const handleEditClient = (clientData: ClientType) => {
        if (!selectedClient) return;

        const { url, method } = buildApiRequestConfig(routes.client.update, [{ param: 'id', value: selectedClient?.id }]);
        const body = {
            abbr: clientData.companyAbbreviation,
            companyName: clientData.company,
            recipient: clientData.recipient,
            address1: clientData.streetAddress1,
            address2: clientData.streetAddress2,
            suburb: clientData.suburb,
            state: clientData.state,
            postalCode: clientData.postalCode,
            country: clientData.country,
            phone: clientData.phone,
            fax: clientData.fax,
            email: clientData.email,
            nextInvoiceNumber: clientData.nextInvoiceNumber,
            provisionDateOffset: clientData.provisionDateOffset,
            isProvisionDateManual: clientData.isProvisionDateManual,
            searchString: clientData.searchString,
        }
        setUpdateClient({ url, method, body });
    };

    const money = (n: number) => (formatAmount ? formatAmount(n) : n.toFixed(2));

    // Total row pinned above the clients - every listed client's invoices
    // combined (so it narrows with the search), with the same chart when expanded.
    const TOTAL_ROW_ID = -1;
    const totalStats = React.useMemo(() => combineClientStats(list.filtered.map(c => invoiceStats[c.id])), [list.filtered, invoiceStats]);
    const tableRows = React.useMemo(() => (canSeeInvoices && Object.keys(invoiceStats).length
        ? [{ id: TOTAL_ROW_ID, isTotal: true, abbr: '', company: 'Total', recipient: '', address: '', phone: '', email: '', fax: '' } as any, ...list.filtered]
        : list.filtered), [canSeeInvoices, invoiceStats, list.filtered]); // eslint-disable-line react-hooks/exhaustive-deps
    const statsFor = (row: Record<string, any>) => (row.id === TOTAL_ROW_ID ? totalStats : invoiceStats[row.id]);

    // Dollars invoiced in the last 12 months; the rest on hover.
    const invoicedColumn = {
        id: 'invoiced12m',
        label: '$ Last 12 Months',
        size: 145,
        type: 'custom' as const,
        mobilePrimary: true,
        mobileOrder: 0,
        mobileWidth: 95,
        customElement: (_value: any, row: Record<string, any>) => {
            const sum = summarizeClientInvoices(statsFor(row));
            const trend = invoiceTrend(statsFor(row));
            return (
                <Tooltip
                    arrow
                    title={
                        <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
                            <div>Last 12 months: {money(sum.last12Amount)} · {sum.last12Count} invoice{sum.last12Count === 1 ? '' : 's'}</div>
                            <div>All time: {money(sum.allAmount)} · {sum.allCount} invoice{sum.allCount === 1 ? '' : 's'}</div>
                            {sum.last12Amount > 0 && (
                                <div>Trend: {trend.direction === 'flat' ? 'flat' : `${trend.direction === 'up' ? 'up' : 'down'} ${money(Math.abs(trend.slope))} a month`}</div>
                            )}
                        </Box>
                    }
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: !sum.last12Amount ? 'var(--color-text-muted)' : TREND_COLORS[trend.direction] }}>
                        {money(sum.last12Amount)}
                    </Typography>
                </Tooltip>
            );
        },
    };

    const columns = [
        ...(canSeeInvoices ? [invoicedColumn] : []),
        { 
            id: 'abbr', 
            label: 'Abbr', 
            size: 70, 
            type: 'string' as const,
            onSort: () => {
                setSortBy('abbr');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        {
            id: 'company',
            label: 'Company Name',
            size: 170,
            type: 'string' as const,
            mobilePrimary: true,
            mobileOrder: 1,
            mobileWidth: 130,
            mobileHideLabel: true,
            onSort: () => {
                setSortBy('companyName');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        {
            id: 'recipient',
            label: 'Recipient',
            size: 120,
            type: 'string' as const,
            mobilePrimary: true,
            mobileOrder: 2,
            mobileWidth: 90,
            onSort: () => {
                setSortBy('recipient');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { id: 'address', label: 'Address', type: 'string' as const },
        {
            id: 'phone',
            label: 'Phone',
            type: 'string' as const,
            size: 110,
            mobilePrimary: !canSeeInvoices,
            mobileOrder: 3,
            mobileWidth: 95,
            onSort: () => {
                setSortBy('phone');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { 
            id: 'fax', 
            label: 'Fax', 
            type: 'string' as const, 
            size: 90,
            onSort: () => {
                setSortBy('fax');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { 
            id: 'email', 
            label: 'Email', 
            size: 160, 
            type: 'string' as const,
            onSort: () => {
                setSortBy('email');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            size: 90, 
            type: 'actions' as const, 
            canEdit: (row: Record<string, any>) => !row.isTotal,
            canDelete: (row: Record<string, any>) => !row.isTotal,
            onDelete: handleDelete,
            onEdit: handleEdit,
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddClientModal
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => {
                    setOpenModal('');
                    setSelectedClient(null);
                }}
                onCreate={openModal === 'add' ? handleCreateClient : handleEditClient}
                isEdit={openModal === 'edit' ? { id: selectedClient?.id || null, state: true } : { id: null, state: false }}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Client?"
                description="This action will permanently remove the selected client."
                image={{ src: clientImage, width: 150, height: 150 }}
                onCancel={() => {
                    setOpenModal('');
                    setSelectedClient(null);
                }}
                onConfirm={handleConfirmDelete}
            />
            <Topbar
                title={<ListTitle title="Clients" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                    {!isMobile && (
                        <>
                            <XeroSyncButton type="clients" />
                            <AddBtn disabled={!canCreate} onClick={() => setOpenModal('add')} label="Add Client" />
                            <IconButton
                                size="small"
                                onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
                                title="Client tools"
                                sx={{ color: 'var(--color-primary)' }}
                            >
                                <Settings size={18} />
                            </IconButton>
                            <MuiMenu
                                anchorEl={settingsMenuAnchor}
                                open={!!settingsMenuAnchor}
                                onClose={() => setSettingsMenuAnchor(null)}
                            >
                                <MenuItem
                                    disabled={!canUpdate || analyzeAll.isLoading}
                                    onClick={() => { setSettingsMenuAnchor(null); handleAnalyzeAll(); }}
                                    title="Suggest a bank description for every client from their own paid transactions - clients that already have one are left alone"
                                    sx={{ fontSize: 14 }}
                                >
                                    {analyzeAll.isLoading ? 'Analyzing…' : 'Analyze Search Strings'}
                                </MenuItem>
                            </MuiMenu>
                        </>
                    )}
                </Box>
            </Topbar>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    columns={columns}
                    data={tableRows}
                    isLoading={fetchClients.isLoading}
                    fitContent
                    scrollMode="page"
                    sortState={{ column: sortBy, direction: sortDirection.toUpperCase() as 'ASC' | 'DESC' }}
                    canEditRows={canUpdate}
                    canDeleteRows={canDelete}
                    renderDetailPanel={canSeeInvoices ? (row) => (
                        <ClientInvoicePanel
                            stats={statsFor(row)}
                            isLoading={fetchInvoiceStats.isLoading}
                            formatAmount={(n) => (formatAmount ? formatAmount(n) : String(n))}
                        />
                    ) : undefined}
                />
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreate && (
                <Fab
                    onClick={() => setOpenModal('add')}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default Clients;